import React, { useEffect, useRef, useState } from 'react';
import { X, UserPlus, Pencil } from 'lucide-react';
import { RECIPIENT_CHANNELS } from '../utils/recipientFields';

/**
 * AddRecipientModal
 *
 * Modal dialog for creating or editing a recipient with all editable fields.
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Function} onClose - Callback to close modal
 * @param {Function} onSave - Callback with recipient fields (create mode)
 * @param {Object} [initial] - When present, modal opens in edit mode, pre-fills, and calls onUpdate
 * @param {Function} [onUpdate] - Callback with updated fields + recipient id (edit mode)
 */
export default function AddRecipientModal({ isOpen, onClose, onSave, initial, onUpdate }) {
    const isEdit = Boolean(initial);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [whatsappId, setWhatsappId] = useState('');
    const [messengerId, setMessengerId] = useState('');
    const [recipientGroup, setRecipientGroup] = useState('');
    const [preferredChannel, setPreferredChannel] = useState('web');
    const [locale, setLocale] = useState('');
    const [timezone, setTimezone] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const emailInputRef = useRef(null);
    const previouslyFocusedRef = useRef(null);

    // Focus email field when modal opens, reset or pre-fill the form
    useEffect(() => {
        if (!isOpen) return;
        previouslyFocusedRef.current = document.activeElement;
        if (isEdit) {
            setEmail(initial.email || '');
            setName(initial.name || '');
            setPhoneNumber(initial.phone_number || '');
            setWhatsappId(initial.whatsapp_id || '');
            setMessengerId(initial.facebook_messenger_id || '');
            setRecipientGroup(initial.recipient_group || '');
            setPreferredChannel(initial.preferred_channel || 'web');
            setLocale(initial.locale || '');
            setTimezone(initial.timezone || '');
        } else {
            setEmail(''); setName(''); setPhoneNumber(''); setWhatsappId(''); setMessengerId('');
            setRecipientGroup(''); setPreferredChannel('web');
            setLocale(''); setTimezone('');
        }
        setError('');
        setSaving(false);
        setTimeout(() => emailInputRef.current?.focus(), 0);
    }, [isOpen, isEdit, initial]);

    // Restore focus when closing
    useEffect(() => {
        if (!isOpen && previouslyFocusedRef.current?.focus) {
            previouslyFocusedRef.current.focus();
        }
    }, [isOpen]);

    // Close on ESC
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    const handleSubmit = async () => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setError('Email is required');
            return;
        }
        // Basic email format check
        if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
            setError('Please enter a valid email address');
            return;
        }

        setSaving(true);
        setError('');
        const fields = {
            email: trimmedEmail,
            name: name.trim() || undefined,
            phone_number: phoneNumber.trim() || undefined,
            whatsapp_id: whatsappId.trim() || undefined,
            facebook_messenger_id: messengerId.trim() || undefined,
            recipient_group: recipientGroup.trim() || undefined,
            preferred_channel: preferredChannel,
            locale: locale.trim() || undefined,
            timezone: timezone.trim() || undefined,
        };
        try {
            if (isEdit) {
                await onUpdate?.({ ...fields, global_recipient_id: initial.global_recipient_id });
            } else {
                await onSave?.(fields);
            }
            onClose?.();
        } catch (err) {
            setError(err?.message || (isEdit ? 'Failed to update recipient.' : 'Failed to create recipient.'));
        } finally {
            setSaving(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose?.();
    };

    const enterSubmit = (e) => { if (e.key === 'Enter') handleSubmit(); };

    if (!isOpen) return null;

    return (
        <div style={styles.overlay} onClick={handleBackdropClick}>
            <div style={styles.modal} role="dialog" aria-labelledby="add-recipient-title" aria-modal="true">
                <div style={styles.header}>
                    <h2 id="add-recipient-title" style={styles.title}>
                        {isEdit
                            ? <Pencil size={20} style={{ marginRight: 8, verticalAlign: -4 }} />
                            : <UserPlus size={20} style={{ marginRight: 8, verticalAlign: -4 }} />}
                        {isEdit ? 'Edit Recipient' : 'Add Recipient'}
                    </h2>
                    <button style={styles.closeButton} onClick={onClose} aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                {/* Two-column grid for fields */}
                <div style={styles.fieldGrid}>
                    <div style={styles.formGroup}>
                        <label htmlFor="add-recipient-email" style={styles.label}>Email *</label>
                        <input
                            ref={emailInputRef}
                            id="add-recipient-email"
                            style={styles.input}
                            type="email"
                            placeholder="recipient@example.com"
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            onKeyPress={enterSubmit}
                            disabled={saving}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="add-recipient-name" style={styles.label}>Name</label>
                        <input
                            id="add-recipient-name"
                            style={styles.input}
                            type="text"
                            placeholder="Full name"
                            value={name} onChange={(e) => setName(e.target.value)}
                            onKeyPress={enterSubmit}
                            disabled={saving}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="add-recipient-phone" style={styles.label}>Phone</label>
                        <input
                            id="add-recipient-phone"
                            style={styles.input}
                            type="tel"
                            placeholder="+1 555 123 4567"
                            value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                            onKeyPress={enterSubmit}
                            disabled={saving}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="add-recipient-whatsapp" style={styles.label}>WhatsApp ID</label>
                        <input
                            id="add-recipient-whatsapp"
                            style={styles.input}
                            type="text"
                            placeholder="WhatsApp ID"
                            value={whatsappId} onChange={(e) => setWhatsappId(e.target.value)}
                            onKeyPress={enterSubmit}
                            disabled={saving}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="add-recipient-messenger" style={styles.label}>Facebook Messenger ID</label>
                        <input
                            id="add-recipient-messenger"
                            style={styles.input}
                            type="text"
                            placeholder="Page-scoped user ID"
                            value={messengerId} onChange={(e) => setMessengerId(e.target.value)}
                            onKeyPress={enterSubmit}
                            disabled={saving}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="add-recipient-group" style={styles.label}>Group</label>
                        <input
                            id="add-recipient-group"
                            style={styles.input}
                            type="text"
                            placeholder="Recipient group"
                            value={recipientGroup} onChange={(e) => setRecipientGroup(e.target.value)}
                            onKeyPress={enterSubmit}
                            disabled={saving}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="add-recipient-channel" style={styles.label}>Preferred Channel</label>
                        <select
                            id="add-recipient-channel"
                            style={styles.input}
                            value={preferredChannel} onChange={(e) => setPreferredChannel(e.target.value)}
                            disabled={saving}
                        >
                            {RECIPIENT_CHANNELS.map(channel => <option key={channel.value} value={channel.value}>{channel.label}</option>)}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label htmlFor="add-recipient-locale" style={styles.label}>Locale</label>
                        <input
                            id="add-recipient-locale"
                            style={styles.input}
                            type="text"
                            placeholder="en-US"
                            value={locale} onChange={(e) => setLocale(e.target.value)}
                            onKeyPress={enterSubmit}
                            disabled={saving}
                        />
                    </div>

                    <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                        <label htmlFor="add-recipient-timezone" style={styles.label}>Timezone</label>
                        <input
                            id="add-recipient-timezone"
                            style={styles.input}
                            type="text"
                            placeholder="America/New_York"
                            value={timezone} onChange={(e) => setTimezone(e.target.value)}
                            onKeyPress={enterSubmit}
                            disabled={saving}
                        />
                    </div>
                </div>

                <div style={styles.actions}>
                    <button
                        style={styles.cancelButton}
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        style={{ ...styles.saveButton, ...(saving && styles.buttonDisabled) }}
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save' : 'Create Recipient')}
                    </button>
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: 8,
        padding: 24,
        width: '100%',
        maxWidth: 560,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    title: {
        margin: 0,
        fontSize: 20,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
    },
    closeButton: {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        lineHeight: 1,
        color: '#5F6368',
        display: 'inline-flex',
    },
    error: {
        backgroundColor: '#FFEBEE',
        color: '#C62828',
        padding: 12,
        borderRadius: 4,
        marginBottom: 16,
        fontSize: 14,
    },
    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
    },
    label: {
        display: 'block',
        fontSize: 12,
        fontWeight: 600,
        color: '#64748b',
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    input: {
        width: '100%',
        padding: '8px 12px',
        border: '1px solid #DADCE0',
        borderRadius: 6,
        fontSize: 14,
        boxSizing: 'border-box',
        outline: 'none',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 24,
    },
    cancelButton: {
        padding: '8px 16px',
        backgroundColor: 'var(--color-bg-base)',
        color: '#3C4043',
        border: '1px solid #DADCE0',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 14,
    },
    saveButton: {
        padding: '8px 20px',
        backgroundColor: '#5B9FFF',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
    },
    buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
};
